import React, { useCallback } from "react"
import { Navigate, type RouteObject, useLocation } from "react-router-dom"

import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { ROLE_IDS } from "../../../../constants/roles.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  selectCompanyIsMultiBranch,
  selectIsOwner
} from "../../../Branches/redux/branches.slice.ts"
import { CompanyDefaultsGate } from "../../../Settings/components/common/CompanyDefaultsGate.tsx"
import {
  allowsBlock,
  allowsSubtab
} from "../../../Settings/components/common/company-defaults-access.ts"
import { lazyPage } from "../lazyPage.tsx"
import OpenInCompanyHQGate from "../../../Branches/components/OpenInCompanyHQGate.tsx"
import {
  AutomationsMovedRedirect,
  EmailTemplatesMovedRedirect
} from "../../../Communication/hub/moved-redirects.tsx"
import { LegacyProjectTemplateRedirect } from "./LegacyTemplateRedirects.tsx"
import { SettingsLayout } from "../../../Settings/components/common/SettingsLayout.tsx"

const SettingsConfigurationPage = lazyPage(
  () => import("../../../Settings/pages/SettingsConfigurationPage.tsx")
)

const SettingsCompanyPage = lazyPage(
  () => import("../../../Settings/pages/SettingsCompanyPage.tsx")
)

const DocumentTemplatesPage = lazyPage(
  () =>
    import("../../../DocumentTemplates/DocumentTemplatesPage/DocumentTemplatesPage.tsx")
)
const EmailTemplatesPage = lazyPage(() =>
  import("../../../EmailTemplates/EmailTemplatesPage.tsx").then((m) => ({
    default: m.EmailTemplatesPage
  }))
)
const SettingNotificationPage = lazyPage(
  () => import("../../../Settings/pages/SettingNotificationPage.tsx")
)
const SettingsBranchDetailPage = lazyPage(
  () => import("../../../Branches/pages/SettingsBranchDetailPage.tsx")
)
const SettingsBranchesPage = lazyPage(
  () => import("../../../Branches/pages/SettingsBranchesPage.tsx")
)
const SettingsFormFieldRequirementsPage = lazyPage(
  () => import("../../../Settings/pages/SettingsFormFieldRequirementsPage.tsx")
)
const SettingsLeadSourceBudgetPage = lazyPage(
  () => import("../../../Settings/pages/SettingsLeadSourceBudgetPage.tsx")
)
const SettingsListsPage = lazyPage(
  () => import("../../../Settings/pages/SettingsListsPage.tsx")
)
const SettingsProfilePage = lazyPage(
  () => import("../../../Settings/pages/SettingsProfilePage.tsx")
)
const SettingsSmsUsagePage = lazyPage(
  () => import("../../../Settings/pages/SettingsSmsUsagePage.tsx")
)
const SettingsStatusAutomationPage = lazyPage(
  () => import("../../../Settings/pages/SettingsStatusAutomationPage.tsx")
)
const SettingsTemplatesPage = lazyPage(
  () => import("../../../Settings/pages/SettingsTemplatesPage.tsx")
)
const AutomationBuilderPage = lazyPage(
  () => import("../../../SettingsAutomation/AutomationBuilderPage.tsx")
)
const AutomationOverviewPage = lazyPage(
  () => import("../../../SettingsAutomation/AutomationOverviewPage.tsx")
)
const DashboardTemplatesSettings = lazyPage(
  () =>
    import("../../../SettingsDashboardTemplates/DashboardTemplatesSettings.tsx")
)
const NewPermissionsSettings = lazyPage(
  () => import("../../../SettingsPermissions/PermissionsSettings.tsx")
)
const ProjectTypePage = lazyPage(
  () => import("../../../Templates/ProjectTypePage")
)
const ProjectTypesTemplatesPage = lazyPage(
  () => import("../../../Templates/ProjectTypesTemplatesPage")
)
// The tab is hidden without the company's lead-automation flag, so every
// Automation route must refuse a direct visit too, not just the permission-less.
function AutomationGate({ children }: { children: React.ReactNode }) {
  const flagOn = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_lead_automation
  )
  const check = useCallback(() => flagOn, [flagOn])
  return (
    <PermissionGate
      paths={["settings.automations.settings_automation_view"]}
      check={check}
    >
      {children}
    </PermissionGate>
  )
}

// Required Information moved under Automation; a bookmark keeps its
// `?entity=` and `?context=`.
function LegacyFormFieldRequirementsRedirect() {
  const { search } = useLocation()
  return (
    <Navigate
      replace
      to={`${RoutesPaths.settings.formFieldRequirements}${search}`}
    />
  )
}

// `role?.id === ADMIN_GCC` alone misses a real owner: `invite_gcc_admin`
// (Django Admin's "Invite GCC Owner" action) deliberately leaves the
// legacy `User.role` at its TEAM_MEMBER default ("TODO refactor as we
// wont have gccadmin role in future", `apps/user/admin.py`) and instead
// grants ownership through `GccAssignment.is_owner` + the "gcc" permission
// label — the same signal `branches-accessible`'s `is_owner` (and this
// selector) already reads. Without this, a Django-provisioned multi-branch
// owner's first visit to Settings → Branches reads "Access Restricted".
//
// `isMultiBranch` (the real `Gcc.multi_branch_enabled` flag, never
// role/branch-count-inferred) is required in the SAME check, not just on
// the nav tab: the tab was already hidden for a single-operation owner,
// but nothing stopped them reaching this route directly by URL/bookmark/
// back-button and seeing "Add branch" and branch terminology they must
// never see at all.
function BranchesOwnerGate({ children }: { children: React.ReactNode }) {
  const isOwner = useAppSelector(selectIsOwner)
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  const check = useCallback(
    (_permissions: unknown, role: { id?: number } | undefined) =>
      isMultiBranch &&
      (isOwner ||
        role?.id === ROLE_IDS.ADMIN_GCC ||
        role?.id === ROLE_IDS.ADMIN_PLATFORM),
    [isOwner, isMultiBranch]
  )
  return (
    <PermissionGate
      description="Only the company owner can manage branches."
      check={check}
    >
      {children}
    </PermissionGate>
  )
}

const allowsConfiguration = allowsSubtab("configuration")
const allowsRequiredInformation = allowsBlock("required_information")
const allowsStatusAutomation = allowsBlock("status_automation")
const allowsTemplates = allowsSubtab("templates")
const allowsProjectTemplates = allowsBlock("project_templates")
const allowsProjectTemplateEdit = allowsBlock("project_templates", "edit")
const allowsDocumentTemplates = allowsBlock("document_templates")
const allowsEmailTemplates = allowsBlock("email_templates")
const allowsDashboardTemplates = allowsBlock("dashboard_templates")
const allowsDropDownLists = allowsBlock("drop_down_lists")
const allowsLeadSourceBudget = allowsBlock("lead_source_budget")

export const settingsRoutes: RouteObject[] = [
  {
    path: RoutesPaths.settings.profile,
    element: <SettingsLayout />,
    children: [
      { index: true, element: <SettingsProfilePage /> },
      {
        path: RoutesPaths.settings.company,
        element: (
          <PermissionGate paths={["settings.company.settings_company_view"]}>
            <SettingsCompanyPage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.templates,
        element: (
          <CompanyDefaultsGate allows={allowsTemplates}>
            <SettingsTemplatesPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.projectTemplates,
        element: (
          <CompanyDefaultsGate allows={allowsProjectTemplates}>
            <ProjectTypesTemplatesPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.projectTemplate(":id"),
        element: (
          <CompanyDefaultsGate allows={allowsProjectTemplateEdit}>
            <ProjectTypePage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.documentTemplates,
        element: (
          <CompanyDefaultsGate allows={allowsDocumentTemplates}>
            <DocumentTemplatesPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.emailTemplates,
        element: (
          <CompanyDefaultsGate allows={allowsEmailTemplates}>
            <EmailTemplatesMovedRedirect fallback={<EmailTemplatesPage />} />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.dashboardTemplates,
        element: (
          <CompanyDefaultsGate allows={allowsDashboardTemplates}>
            <DashboardTemplatesSettings />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.lists,
        element: (
          <CompanyDefaultsGate allows={allowsDropDownLists}>
            <SettingsListsPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.leadSourceBudget,
        element: (
          <CompanyDefaultsGate allows={allowsLeadSourceBudget}>
            <SettingsLeadSourceBudgetPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.formFieldRequirements,
        element: (
          <CompanyDefaultsGate allows={allowsRequiredInformation}>
            <SettingsFormFieldRequirementsPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.legacyFormFieldRequirements,
        element: <LegacyFormFieldRequirementsRedirect />
      },
      {
        path: "/settings/document_templates",
        element: (
          <Navigate replace to={RoutesPaths.settings.documentTemplates} />
        )
      },
      {
        path: "/settings/dashboard_templates",
        element: (
          <Navigate replace to={RoutesPaths.settings.dashboardTemplates} />
        )
      },
      {
        path: "/settings/templates/:id",
        element: <LegacyProjectTemplateRedirect />
      },
      {
        path: RoutesPaths.settings.permissions,
        element: (
          <PermissionGate
            paths={["settings.permissions.settings_permissions_view"]}
          >
            <NewPermissionsSettings />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.notifications,
        element: <SettingNotificationPage />
      },
      {
        path: RoutesPaths.settings.configuration,
        element: (
          <CompanyDefaultsGate allows={allowsConfiguration}>
            <SettingsConfigurationPage />
          </CompanyDefaultsGate>
        )
      },
      {
        // No hideOnDenied here: a branch-restricted manager typing this URL
        // directly must see the app's real "not allowed" experience, not a
        // blank tab-less page.
        path: RoutesPaths.settings.branches,
        element: (
          <BranchesOwnerGate>
            <OpenInCompanyHQGate>
              <SettingsBranchesPage />
            </OpenInCompanyHQGate>
          </BranchesOwnerGate>
        )
      },
      {
        path: RoutesPaths.settings.branch(":id"),
        element: (
          <BranchesOwnerGate>
            <OpenInCompanyHQGate>
              <SettingsBranchDetailPage />
            </OpenInCompanyHQGate>
          </BranchesOwnerGate>
        )
      },
      {
        path: RoutesPaths.settings.smsUsage,
        element: (
          <PermissionGate
            hideOnDenied
            check={(permissions, role) =>
              role?.id === ROLE_IDS.ADMIN_PLATFORM ||
              !!permissions?.codes?.settings?.company?.settings_company_view
            }
          >
            <SettingsSmsUsagePage />
          </PermissionGate>
        )
      },
      {
        path: RoutesPaths.settings.automation,
        element: (
          <AutomationsMovedRedirect
            mode="list"
            fallback={
              <AutomationGate>
                <AutomationOverviewPage />
              </AutomationGate>
            }
          />
        )
      },
      {
        path: RoutesPaths.settings.statusAutomation,
        element: (
          <CompanyDefaultsGate allows={allowsStatusAutomation}>
            <SettingsStatusAutomationPage />
          </CompanyDefaultsGate>
        )
      },
      {
        path: RoutesPaths.settings.automationNew,
        element: (
          <AutomationsMovedRedirect
            mode="new"
            fallback={
              <AutomationGate>
                <AutomationBuilderPage />
              </AutomationGate>
            }
          />
        )
      },
      {
        path: RoutesPaths.settings.automationEdit(":id"),
        element: (
          <AutomationsMovedRedirect
            mode="edit"
            fallback={
              <AutomationGate>
                <AutomationBuilderPage />
              </AutomationGate>
            }
          />
        )
      }
    ]
  }
]
